// D65 — Page /engins/{id} : détail + indicateurs + interventions
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { enginsApi, interventionsApi } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { API_BASE } from "@/lib/apiBase";
import { ht } from "@/lib/ht";
import { useT } from "@/lib/i18n";

export default function DetailEnginPage() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const enginId = Number(id);

  const { data: engin } = useQuery({
    queryKey: ["engin", enginId],
    queryFn: () => enginsApi.get(enginId),
    enabled: !!enginId,
  });
  const { data: interventions } = useQuery({
    queryKey: ["interventions", { engin_id: enginId }],
    queryFn: () => interventionsApi.list({ engin_id: enginId }),
    enabled: !!enginId,
  });

  if (!engin) return <div className="p-8">{ht("Chargement...")}</div>;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/engins" className="text-sm text-muted-foreground hover:underline">
            {t("maintenance.back_to_list")}
          </Link>
          <h1 className="font-mono text-2xl font-bold">{engin.code}</h1>
        </div>
        <Button
          variant="outline"
          onClick={() =>
            window.open(
              `${API_BASE}/v1/exports/engins/${enginId}/pdf`,
              "_blank",
            )
          }
        >
          {t("engins.export_pdf_history")}
        </Button>
      </div>

      {/* Caractéristiques */}
      <Card>
        <CardHeader>
          <CardTitle>{ht("Caractéristiques")}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
          <Field label="Code" value={engin.code} />
          <Field label={ht("Immatriculation")} value={engin.immatriculation ?? "-"} />
          <Field label={ht("N° parc")} value={engin.n_parc ?? "-"} />
          <Field label={ht("N° châssis")} value={engin.n_chassis ?? "-"} />
          <Field label={ht("Type engin")} value={`#${engin.type_engin_id}`} />
          <Field label={t("common.site")} value={engin.site_id ? `#${engin.site_id}` : "-"} />
          <Field label={t("common.contract")} value={engin.contrat_id ? `#${engin.contrat_id}` : "-"} />
          <Field
            label={t("common.active")}
            value={engin.is_active ? t("common.yes") : t("common.no")}
          />
        </CardContent>
      </Card>

      {/* Compteurs */}
      <Card>
        <CardHeader>
          <CardTitle>{ht("Compteurs / Niveaux")}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-3 text-sm md:grid-cols-6">
          <Field label={ht("Kilométrage")} value={`${engin.kilometrage.toLocaleString("fr-FR")} km`} />
          <Field label={ht("Compteur horaire")} value={`${engin.compteur_horaire} h`} />
          <Field label={ht("Huile moteur")} value={`${engin.huile_moteur}%`} />
          <Field label={ht("Huile boîte")} value={`${engin.huile_boite}%`} />
          <Field label={ht("Eau radiateur")} value={`${engin.eau_radiateur}%`} />
          <Field label={ht("Graissage")} value={`${engin.graissage}%`} />
        </CardContent>
      </Card>

      {/* Historique interventions */}
      <Card>
        <CardHeader>
          <CardTitle>
            {t("engins.history_title")} ({interventions?.items.length ?? 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {interventions?.items.length === 0 ? (
            <p className="text-sm text-muted-foreground">{ht("Aucune intervention pour cet engin.")}</p>
          ) : (
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                  <th className="px-3 py-2">Code</th>
                  <th className="px-3 py-2">{ht("Type")}</th>
                  <th className="px-3 py-2">{ht("Statut")}</th>
                  <th className="px-3 py-2">{ht("Date début")}</th>
                  <th className="px-3 py-2">{ht("Résumé")}</th>
                </tr>
              </thead>
              <tbody>
                {interventions?.items.map((i) => (
                  <tr key={i.id} className="border-b">
                    <td className="px-3 py-2 font-mono">
                      <Link to={`/maintenance/${i.id}`} className="hover:underline">
                        {i.intervention_code}
                      </Link>
                    </td>
                    <td className="px-3 py-2">{i.type}</td>
                    <td className="px-3 py-2">{i.statut}</td>
                    <td className="px-3 py-2 text-xs">{new Date(i.date_debut).toLocaleDateString("fr-FR")}</td>
                    <td className="px-3 py-2">{i.resume ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase text-muted-foreground">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}
